import React, { useState, useEffect, useRef } from 'react';
import { ExternalLink, Music, Calendar, Upload, Plus, X, Building2, CheckCircle, Smile, CalendarPlus, Search, ChevronDown, Trophy, Loader2, RotateCcw } from 'lucide-react';
import EmotePicker from '../EmotePicker';

// Import extracted components
import { BeatSeekContestCard, EditContestModal, EditDemoRequestModal } from './DemoDrops';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ContestsSection Component with Sub-tabs
const ContestsSection = ({ remixContests, searchTerm, genreFilter, contestAddingToCalendar, contestAddedToCalendar, handleAddContestToCalendar, currentUser, onEditContest, onDeleteContest, onRefreshContests }) => {
  const [contestSubTab, setContestSubTab] = useState('beatseek');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Contest Sub-tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        flexWrap: 'wrap',
        marginBottom: '0.5rem',
        borderBottom: '1px solid rgba(167, 139, 250, 0.2)',
        paddingBottom: '0.75rem'
      }}>
        <button
          onClick={() => setContestSubTab('beatseek')}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '8px 8px 0 0',
            border: 'none',
            background: contestSubTab === 'beatseek' 
              ? 'rgba(167, 139, 250, 0.2)' 
              : 'transparent',
            color: contestSubTab === 'beatseek' ? '#a78bfa' : 'rgba(255,255,255,0.6)',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: contestSubTab === 'beatseek' ? '600' : '400',
            borderBottom: contestSubTab === 'beatseek' ? '2px solid #a78bfa' : '2px solid transparent',
            transition: 'all 0.2s ease'
          }}
        >
          BeatSeek Contests
        </button>
        <button
          onClick={() => setContestSubTab('labelradar')}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: '8px 8px 0 0',
            border: 'none',
            background: contestSubTab === 'labelradar' 
              ? 'rgba(251, 191, 36, 0.2)' 
              : 'transparent',
            color: contestSubTab === 'labelradar' ? '#fbbf24' : 'rgba(255,255,255,0.6)',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: contestSubTab === 'labelradar' ? '600' : '400',
            borderBottom: contestSubTab === 'labelradar' ? '2px solid #fbbf24' : '2px solid transparent',
            transition: 'all 0.2s ease'
          }}
        >
          LabelRadar Contests
        </button>
      </div>

      {contestSubTab === 'beatseek' ? (
        /* BeatSeek Contests */
        <div>
          {remixContests.filter(contest => {
            if (searchTerm) {
              const search = searchTerm.toLowerCase();
              const matchTitle = contest.title?.toLowerCase().includes(search);
              const matchDesc = contest.description?.toLowerCase().includes(search);
              const matchLabel = contest.label_name?.toLowerCase().includes(search);
              if (!matchTitle && !matchDesc && !matchLabel) return false;
            }
            if (genreFilter !== 'all') {
              const hasGenre = contest.genres?.some(g => g.toLowerCase().includes(genreFilter.toLowerCase()));
              if (!hasGenre) return false;
            }
            return true;
          }).length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1.25rem'
            }}>
              {remixContests.filter(contest => {
                if (searchTerm) {
                  const search = searchTerm.toLowerCase();
                  const matchTitle = contest.title?.toLowerCase().includes(search);
                  const matchDesc = contest.description?.toLowerCase().includes(search);
                  const matchLabel = contest.label_name?.toLowerCase().includes(search);
                  if (!matchTitle && !matchDesc && !matchLabel) return false;
                }
                if (genreFilter !== 'all') {
                  const hasGenre = contest.genres?.some(g => g.toLowerCase().includes(genreFilter.toLowerCase()));
                  if (!hasGenre) return false;
                }
                return true;
              }).map((contest) => {
                const isOwner = currentUser && (
                  currentUser.id === contest.user_id || 
                  currentUser.user_id === contest.user_id ||
                  currentUser.id === contest.created_by ||
                  currentUser.user_id === contest.created_by
                );
                return (
                  <BeatSeekContestCard
                    key={contest.id}
                    contest={contest}
                    isOwner={isOwner}
                    onEditContest={onEditContest}
                    onDeleteContest={onDeleteContest}
                    contestAddingToCalendar={contestAddingToCalendar}
                    contestAddedToCalendar={contestAddedToCalendar}
                    handleAddContestToCalendar={handleAddContestToCalendar}
                  />
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <Trophy size={48} style={{ color: 'rgba(255,255,255,0.3)', marginBottom: '1rem' }} />
              <p style={{ color: 'rgba(255,255,255,0.5)' }}>No contests posted at the moment</p>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>
                Labels can post contests for producers to enter
              </p>
            </div>
          )}
        </div>
      ) : (
        /* LabelRadar Contests */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <a
            href="https://www.labelradar.com/artists/to-contests"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(147, 51, 234, 0.15))',
              border: '2px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '12px',
              textDecoration: 'none',
              transition: 'all 0.3s ease'
            }}
          >
            <div>
              <h4 style={{ color: '#fbbf24', fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>
                🌐 Browse LabelRadar Contests
              </h4>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', margin: '0.5rem 0 0 0' }}>
                Official remix and production contests from major labels worldwide
              </p>
              <p style={{ color: 'rgba(251, 191, 36, 0.7)', fontSize: '0.75rem', margin: '0.5rem 0 0 0', fontStyle: 'italic' }}>
                * Must create a LabelRadar account or sign in to view
              </p>
            </div>
            <ExternalLink size={24} style={{ color: '#fbbf24' }} />
          </a>
          
          <div style={{ 
            background: 'rgba(30, 20, 50, 0.3)', 
            borderRadius: '12px', 
            padding: '1.5rem',
            border: '1px solid rgba(251, 191, 36, 0.1)'
          }}>
            <h5 style={{ color: '#fbbf24', fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.75rem' }}>
              What you&apos;ll find on LabelRadar:
            </h5>
            <ul style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', margin: 0, paddingLeft: '1.25rem', lineHeight: '1.8' }}>
              <li>Official remix contests from top labels</li>
              <li>Production challenges with prizes</li>
              <li>Opportunities to get signed</li>
              <li>Direct submission to A&amp;R teams</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

const DemoDropsTab = () => {
  const [activeSubTab, setActiveSubTab] = useState('showcase');
  const [demos, setDemos] = useState([]);
  const [demoRequests, setDemoRequests] = useState([]);
  const [remixContests, setRemixContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showContestModal, setShowContestModal] = useState(false);
  const [editingContest, setEditingContest] = useState(null);
  const [editingRequest, setEditingRequest] = useState(null);
  const [user, setUser] = useState(null);
  const [showEmotePickerShowcase, setShowEmotePickerShowcase] = useState(false);
  const [showEmotePickerRequest, setShowEmotePickerRequest] = useState(false);
  const [addingToCalendar, setAddingToCalendar] = useState(null); // Track which request is being added
  const [addedToCalendar, setAddedToCalendar] = useState([]); // Track which requests have been added
  const [contestAddingToCalendar, setContestAddingToCalendar] = useState(null);
  const [contestAddedToCalendar, setContestAddedToCalendar] = useState([]);
  
  // Demo showcase form state
  const [demoTitle, setDemoTitle] = useState('');
  const [demoDescription, setDemoDescription] = useState('');
  const [demoGenre, setDemoGenre] = useState('');
  
  // Track preview state (like Forum Track Feedback)
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [clipStartTime, setClipStartTime] = useState(0);
  const [clipEndTime, setClipEndTime] = useState(45);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);
  const [audioUploadId, setAudioUploadId] = useState(null);
  const [userQuota, setUserQuota] = useState({ current: 0, max: 2 });
  const audioPlayerRef = useRef(null);
  
  const [submitting, setSubmitting] = useState(false);

  // Demo request form state (for Labels/A&R)
  const [requestTitle, setRequestTitle] = useState('');
  const [requestDescription, setRequestDescription] = useState('');
  const [requestGenres, setRequestGenres] = useState('');
  const [requestDeadline, setRequestDeadline] = useState('');
  const [requestLabelName, setRequestLabelName] = useState('');
  const [requestContactEmail, setRequestContactEmail] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);

  // Remix contest form state
  const [contestTitle, setContestTitle] = useState('');
  const [contestDescription, setContestDescription] = useState('');
  const [contestGenres, setContestGenres] = useState('');
  const [contestDeadline, setContestDeadline] = useState('');
  const [contestPrize, setContestPrize] = useState('');
  const [contestRules, setContestRules] = useState('');
  const [contestSubmitting, setContestSubmitting] = useState(false);

  // Default demo opportunities (external labels)
  const defaultDemos = [
    {
      id: 1,
      title: "Spinnin' Records - Demo Submissions",
      url: "https://spinninrecords.com/demos",
      description: "Submit your tracks to one of the world's leading electronic music labels.",
      deadline: "Rolling submissions",
      genres: ["Progressive House", "Future House", "Tech House"],
      type: 'external'
    },
    {
      id: 2,
      title: "Anjunabeats - Demo Portal",
      url: "https://anjunabeats.com/demos",
      description: "Submit progressive and trance tracks to Above & Beyond's legendary label.",
      deadline: "Open submissions",
      genres: ["Trance", "Progressive", "Melodic Techno"],
      type: 'external'
    },
    {
      id: 3,
      title: "Monstercat - Demo Submission",
      url: "https://monstercat.com/demos",
      description: "Join the Monstercat family! Home to innovative electronic music.",
      deadline: "Always open",
      genres: ["Dubstep", "Future Bass", "DnB", "House"],
      type: 'external'
    }
  ];

  // State for highlighted demo request (from deep link)
  const [highlightedRequestId, setHighlightedRequestId] = useState(null);
  
  // Search/Filter state for Demo Showcase and Requests
  const [searchTerm, setSearchTerm] = useState('');
  const [genreFilter, setGenreFilter] = useState('all');

  useEffect(() => {
    loadUserInfo();
    loadContent();
  }, [activeSubTab]);

  useEffect(() => {
    if (activeSubTab === 'showcase') {
      loadUserQuota();
    }
  }, [activeSubTab]);

  // Listen for deep link events from Gig Hub calendar
  useEffect(() => {
    const handleDemoRequestDeepLink = (event) => {
      const { requestId } = event.detail || {};
      if (requestId) {
        console.log('📋 Opening demo request from deep link:', requestId);
        // Switch to requests tab
        setActiveSubTab('requests');
        // Highlight the specific request
        setHighlightedRequestId(requestId);
        // Remove highlight after 5 seconds
        setTimeout(() => setHighlightedRequestId(null), 5000);
      }
    };

    window.addEventListener('producerCornerOpenDemoRequest', handleDemoRequestDeepLink);
    return () => {
      window.removeEventListener('producerCornerOpenDemoRequest', handleDemoRequestDeepLink);
    };
  }, []);

  const loadUserInfo = async () => {
    try {
      // First try localStorage
      const storedUser = localStorage.getItem('beatseek_user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
        } catch (e) {
          console.error('Error parsing stored user:', e);
        }
      }
      
      // Then try API for fresh data
      const token = localStorage.getItem('beatseek_token');
      if (token) {
        const response = await fetch(`${BACKEND_URL}/api/users/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      }
    } catch (error) {
      console.error('Error loading user info:', error);
    }
  };

  const loadUserQuota = async () => {
    try {
      const token = localStorage.getItem('beatseek_token');
      if (!token) return;
      
      const response = await fetch(`${BACKEND_URL}/api/forum/audio/quota`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserQuota({ current: data.active_count || 0, max: data.max_allowed || 2 });
      }
    } catch (error) {
      console.error('Error loading quota:', error);
    }
  };

  const loadContent = async () => {
    setLoading(true);
    try {
      if (activeSubTab === 'showcase') {
        await loadShowcases();
      } else if (activeSubTab === 'requests') {
        await loadRequests();
      } else if (activeSubTab === 'contests') {
        await loadContests();
      }
    } finally {
      setLoading(false);
    }
  };

  const loadShowcases = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/producer/demo-showcases`);
      if (response.ok) {
        const data = await response.json();
        setDemos([...data, ...defaultDemos]);
      } else {
        setDemos(defaultDemos);
      }
    } catch (error) {
      console.error('Error loading demo showcases:', error);
      setDemos(defaultDemos);
    }
  };

  const loadRequests = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/producer/demo-requests`);
      if (response.ok) {
        const data = await response.json();
        setDemoRequests(data);
      } else {
        setDemoRequests([]);
      }
    } catch (error) {
      console.error('Error loading demo requests:', error);
      setDemoRequests([]);
    }
  };

  const loadContests = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/remix-contests`);
      if (response.ok) {
        const data = await response.json();
        setRemixContests(data.contests || []);
      } else {
        setRemixContests([]);
      }
    } catch (error) {
      console.error('Error loading remix contests:', error);
      setRemixContests([]);
    }
  };

  const handleAddContestToCalendar = async (contestId) => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please login to add to calendar');
      return;
    }

    setContestAddingToCalendar(contestId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/remix-contests/${contestId}/add-to-calendar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setContestAddedToCalendar(prev => [...prev, contestId]);
        alert('🗓️ Contest deadline added to your calendar! You\'ll get a reminder 48 hours before.');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to add to calendar');
      }
    } catch (error) {
      console.error('Error adding to calendar:', error);
      alert('Failed to add to calendar');
    } finally {
      setContestAddingToCalendar(null);
    }
  };

  const handleSubmitContest = async () => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please login to post a contest');
      return;
    }

    if (!contestTitle.trim() || !contestDeadline) {
      alert('Please fill in at least title and deadline');
      return;
    }

    setContestSubmitting(true);
    try {
      const genresArray = contestGenres.split(',').map(g => g.trim()).filter(g => g);
      
      const response = await fetch(`${BACKEND_URL}/api/remix-contests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: contestTitle,
          description: contestDescription,
          genres: genresArray,
          deadline: contestDeadline,
          prize: contestPrize,
          rules: contestRules
        })
      });

      if (response.ok) {
        setShowContestModal(false);
        setContestTitle('');
        setContestDescription('');
        setContestGenres('');
        setContestDeadline('');
        setContestPrize('');
        setContestRules('');
        await loadContests();
        alert('🎉 Contest posted successfully!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to post contest');
      }
    } catch (error) {
      console.error('Error posting contest:', error);
      alert('Failed to post contest');
    } finally {
      setContestSubmitting(false);
    }
  };

  const handleAddToCalendar = async (requestId) => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please login to add this to your calendar');
      return;
    }

    setAddingToCalendar(requestId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/producer/demo-requests/${requestId}/add-to-calendar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setAddedToCalendar(prev => [...prev, requestId]);
        alert(result.message || 'Added to your Gig & Event Hub calendar! You\'ll receive a reminder 48 hours before the deadline.');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to add to calendar');
      }
    } catch (error) {
      console.error('Error adding to calendar:', error);
      alert('Error adding to calendar. Please try again.');
    } finally {
      setAddingToCalendar(null);
    }
  };

  const handleOpenLink = (url) => {
    if (url) window.open(url, '_blank');
  };

  const canPostRequest = () => {
    return user && (user.user_type === 'label' || user.user_type === 'ar' || user.role === 'admin');
  };

  // Audio handling functions
  const handleAudioFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File size too large. Maximum size is 10MB.');
      return;
    }

    const allowedTypes = ['audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/m4a', 'audio/aac', 'audio/x-m4a'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|m4a|aac)$/i)) {
      alert('Invalid file type. Please upload MP3, WAV, M4A, or AAC files.');
      return;
    }

    setAudioFile(file);
    setAudioPreviewUrl(URL.createObjectURL(file));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const playClipPreview = () => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.currentTime = clipStartTime;
      audioPlayerRef.current.play();
      setTimeout(() => {
        if (audioPlayerRef.current) audioPlayerRef.current.pause();
      }, 45000);
    }
  };

  const handleAudioUpload = async () => {
    if (!audioFile || !termsAccepted) return;

    if (userQuota.current >= userQuota.max) {
      alert(`You have reached the maximum of ${userQuota.max} active track previews.`);
      return;
    }

    setAudioUploading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const formData = new FormData();
      formData.append('file', audioFile);

      const response = await fetch(`${BACKEND_URL}/api/forum/audio/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setAudioUploadId(data.id);
        
        if (clipStartTime > 0) {
          await fetch(`${BACKEND_URL}/api/forum/audio/${data.id}/clip`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ clip_start_time: clipStartTime, clip_duration: 45 })
          });
        }
        
        alert('Track preview added successfully!');
      } else {
        const error = await response.json();
        alert(`Upload failed: ${error.detail || 'Please try again'}`);
      }
    } catch (error) {
      console.error('Error uploading audio:', error);
      alert('Error uploading audio. Please try again.');
    } finally {
      setAudioUploading(false);
    }
  };

  const submitDemoShowcase = async () => {
    if (!demoTitle.trim() || !demoDescription.trim()) {
      alert('Please fill in title and description');
      return;
    }

    if (!audioUploadId) {
      alert('Please add a track preview for your demo');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/producer/demo-showcases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: demoTitle.trim(),
          description: demoDescription.trim(),
          genre: demoGenre.trim(),
          link: `audio_preview:${audioUploadId}`,
          audio_preview_id: audioUploadId
        })
      });

      if (response.ok) {
        const newDemo = await response.json();
        if (newDemo.moderation_status === 'pending_moderation') {
          alert('⏳ Your demo has been sent to admin for approval.');
        } else {
          setDemos(prev => [newDemo, ...prev]);
          alert('Demo posted successfully!');
        }
        resetShowcaseForm();
      } else {
        const error = await response.json();
        alert(`Failed to post demo: ${error.detail || 'Please try again'}`);
      }
    } catch (error) {
      console.error('Error posting demo:', error);
      alert('Error posting demo. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitDemoRequest = async () => {
    if (!requestTitle.trim() || !requestDescription.trim() || !requestLabelName.trim() || !requestContactEmail.trim()) {
      alert('Please fill in all required fields (Title, Description, Label/A&R Name, and Contact Email)');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(requestContactEmail.trim())) {
      alert('Please enter a valid email address');
      return;
    }

    setRequestSubmitting(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const genresArray = requestGenres.split(',').map(g => g.trim()).filter(g => g);
      
      const response = await fetch(`${BACKEND_URL}/api/producer/demo-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: requestTitle.trim(),
          description: requestDescription.trim(),
          genres: genresArray,
          deadline: requestDeadline || null,
          label_name: requestLabelName.trim(),
          contact_email: requestContactEmail.trim()
        })
      });

      if (response.ok) {
        const newRequest = await response.json();
        if (newRequest.moderation_status === 'pending_moderation') {
          alert('⏳ Your demo request has been sent to admin for approval.');
        } else {
          setDemoRequests(prev => [newRequest, ...prev]);
          alert('Demo request posted successfully!');
        }
        resetRequestForm();
      } else {
        const error = await response.json();
        alert(`Failed to post request: ${error.detail || 'Please try again'}`);
      }
    } catch (error) {
      console.error('Error posting request:', error);
      alert('Error posting request. Please try again.');
    } finally {
      setRequestSubmitting(false);
    }
  };

  const resetShowcaseForm = () => {
    setShowUploadModal(false);
    setDemoTitle('');
    setDemoDescription('');
    setDemoGenre('');
    setAudioFile(null);
    setAudioPreviewUrl(null);
    setAudioUploadId(null);
    setClipStartTime(0);
    setClipEndTime(45);
    setTermsAccepted(false);
    setAudioDuration(0);
    setShowEmotePickerShowcase(false);
  };

  const resetRequestForm = () => {
    setShowRequestModal(false);
    setRequestTitle('');
    setRequestDescription('');
    setRequestGenres('');
    setRequestDeadline('');
    setRequestLabelName('');
    setRequestContactEmail('');
    setShowEmotePickerRequest(false);
  };

  const handleEmoteSelectShowcase = (emoteCode) => {
    setDemoDescription(prev => prev + emoteCode);
    setShowEmotePickerShowcase(false);
  };

  const handleEmoteSelectRequest = (emoteCode) => {
    setRequestDescription(prev => prev + emoteCode);
    setShowEmotePickerRequest(false);
  };

  return (
    <div className="demo-drops-tab">
      {/* Sub-Tab Navigation */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setActiveSubTab('showcase')}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '10px',
              border: activeSubTab === 'showcase' ? '1px solid #9333ea' : '1px solid rgba(147, 51, 234, 0.3)',
              background: activeSubTab === 'showcase' ? 'rgba(147, 51, 234, 0.2)' : 'rgba(30, 20, 50, 0.5)',
              color: activeSubTab === 'showcase' ? '#c4b5fd' : 'rgba(255,255,255,0.7)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Upload size={16} /> Demo Showcase
          </button>
          <button
            onClick={() => setActiveSubTab('requests')}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '10px',
              border: activeSubTab === 'requests' ? '1px solid #fbbf24' : '1px solid rgba(251, 191, 36, 0.3)',
              background: activeSubTab === 'requests' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(30, 20, 50, 0.5)',
              color: activeSubTab === 'requests' ? '#fbbf24' : 'rgba(255,255,255,0.7)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Building2 size={16} /> Demo Requests
          </button>
          <button
            onClick={() => setActiveSubTab('contests')}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '10px',
              border: activeSubTab === 'contests' ? '1px solid #a78bfa' : '1px solid rgba(167, 139, 250, 0.3)',
              background: activeSubTab === 'contests' ? 'rgba(167, 139, 250, 0.2)' : 'rgba(30, 20, 50, 0.5)',
              color: activeSubTab === 'contests' ? '#a78bfa' : 'rgba(255,255,255,0.7)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Trophy size={16} /> Contests
          </button>
        </div>

        {/* Action Button */}
        {activeSubTab === 'showcase' ? (
          <button
            onClick={() => {
              const token = localStorage.getItem('beatseek_token');
              if (!token) {
                alert('Please login to share your demo');
                return;
              }
              setShowUploadModal(true);
            }}
            style={{
              padding: '0.75rem 1.25rem',
              borderRadius: '10px',
              border: '1px solid #9333ea',
              background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.2), rgba(251, 191, 36, 0.2))',
              color: '#c4b5fd',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Plus size={16} /> Share Your Demo
          </button>
        ) : activeSubTab === 'requests' ? (
          canPostRequest() ? (
            <button
              onClick={() => setShowRequestModal(true)}
              style={{
                padding: '0.75rem 1.25rem',
                borderRadius: '10px',
                border: '1px solid #fbbf24',
                background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(147, 51, 234, 0.2))',
                color: '#fbbf24',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Plus size={16} /> Post Demo Request
            </button>
          ) : (
            <div style={{ 
              padding: '0.5rem 1rem', 
              background: 'rgba(147, 51, 234, 0.1)', 
              borderRadius: '8px',
              fontSize: '0.75rem',
              color: 'rgba(255,255,255,0.5)'
            }}>
              🎵 Labels & A&R can post requests
            </div>
          )
        ) : activeSubTab === 'contests' ? (
          (user?.user_type === 'label' || user?.user_type === 'ar' || user?.role === 'admin') ? (
            <button
              onClick={() => setShowContestModal(true)}
              style={{
                padding: '0.75rem 1.25rem',
                borderRadius: '10px',
                border: '1px solid #a78bfa',
                background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.2), rgba(251, 191, 36, 0.2))',
                color: '#a78bfa',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Plus size={16} /> Post Contest
            </button>
          ) : (
            <div style={{ 
              padding: '0.5rem 1rem', 
              background: 'rgba(167, 139, 250, 0.1)', 
              borderRadius: '8px',
              fontSize: '0.75rem',
              color: 'rgba(255,255,255,0.5)'
            }}>
              🏆 Labels can post contests
            </div>
          )
        ) : null}
      </div>

      {/* Search/Filter Section */}
      <div style={{ 
        display: 'flex', 
        gap: '0.75rem', 
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Search */}
        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
          <Search size={16} style={{ 
            position: 'absolute', 
            left: '12px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: 'rgba(147, 51, 234, 0.5)' 
          }} />
          <input
            type="text"
            placeholder={activeSubTab === 'showcase' ? "Search demos..." : activeSubTab === 'requests' ? "Search requests..." : "Search contests..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.6rem 0.75rem 0.6rem 2.5rem',
              borderRadius: '8px',
              border: '1px solid rgba(147, 51, 234, 0.3)',
              background: 'rgba(30, 20, 50, 0.8)',
              color: '#fff',
              fontSize: '0.85rem',
              outline: 'none'
            }}
          />
        </div>

        {/* Genre Filter */}
        <div style={{ position: 'relative' }}>
          <select
            value={genreFilter}
            onChange={(e) => setGenreFilter(e.target.value)}
            style={{
              padding: '0.5rem 2rem 0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid rgba(147, 51, 234, 0.3)',
              background: 'rgba(30, 20, 50, 0.8)',
              color: '#fff',
              fontSize: '0.8rem',
              outline: 'none',
              cursor: 'pointer',
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              minWidth: '120px'
            }}
          >
            <option value="all" style={{ background: '#1a1a2e' }}>All Genres</option>
            <option value="techno" style={{ background: '#1a1a2e' }}>Techno</option>
            <option value="house" style={{ background: '#1a1a2e' }}>House</option>
            <option value="tech house" style={{ background: '#1a1a2e' }}>Tech House</option>
            <option value="deep house" style={{ background: '#1a1a2e' }}>Deep House</option>
            <option value="progressive" style={{ background: '#1a1a2e' }}>Progressive</option>
            <option value="trance" style={{ background: '#1a1a2e' }}>Trance</option>
            <option value="drum & bass" style={{ background: '#1a1a2e' }}>Drum & Bass</option>
            <option value="dubstep" style={{ background: '#1a1a2e' }}>Dubstep</option>
          </select>
          <ChevronDown size={14} style={{ 
            position: 'absolute', 
            right: '8px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: 'rgba(147, 51, 234, 0.5)'
          }} />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '32px', height: '32px', border: '3px solid rgba(147,51,234,0.3)', borderTopColor: '#9333ea', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
            <p style={{ color: 'rgba(255,255,255,0.5)' }}>Loading...</p>
          </div>
        </div>
      ) : activeSubTab === 'showcase' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {demos.filter(demo => {
            // Apply search filter
            if (searchTerm) {
              const search = searchTerm.toLowerCase();
              const matchTitle = demo.title?.toLowerCase().includes(search);
              const matchDesc = demo.description?.toLowerCase().includes(search);
              const matchArtist = demo.artist_name?.toLowerCase().includes(search);
              if (!matchTitle && !matchDesc && !matchArtist) return false;
            }
            // Apply genre filter
            if (genreFilter !== 'all') {
              const hasGenre = demo.genres?.some(g => g.toLowerCase().includes(genreFilter.toLowerCase()));
              if (!hasGenre) return false;
            }
            return true;
          }).map((demo) => (
            <div
              key={demo.id}
              onClick={() => handleOpenLink(demo.url || demo.link)}
              style={{
                background: 'rgba(30, 20, 50, 0.5)',
                border: '1px solid rgba(147, 51, 234, 0.2)',
                borderRadius: '12px',
                padding: '1.25rem',
                cursor: demo.url || demo.link ? 'pointer' : 'default',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(147, 51, 234, 0.5)';
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(147, 51, 234, 0.2)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <h4 style={{ color: '#fff', fontSize: '1rem', fontWeight: '600' }}>{demo.title}</h4>
                    {demo.type === 'external' && (
                      <span style={{ background: 'rgba(147, 51, 234, 0.3)', color: '#c4b5fd', padding: '2px 8px', borderRadius: '4px', fontSize: '0.65rem' }}>LABEL</span>
                    )}
                    {demo.type === 'user' && (
                      <span style={{ background: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', padding: '2px 8px', borderRadius: '4px', fontSize: '0.65rem' }}>COMMUNITY</span>
                    )}
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem', marginBottom: '0.75rem', lineHeight: '1.5' }}>{demo.description}</p>
                  {demo.deadline && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginBottom: '0.75rem' }}>
                      <Calendar size={14} />
                      <span>Deadline: {demo.deadline}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {(demo.genres || [demo.genre]).filter(Boolean).map((genre, index) => (
                      <span key={index} style={{ background: 'rgba(147, 51, 234, 0.2)', color: '#c4b5fd', padding: '4px 10px', borderRadius: '6px', fontSize: '0.7rem' }}>{genre}</span>
                    ))}
                  </div>
                </div>
                {(demo.url || demo.link) && <ExternalLink size={18} style={{ color: '#9333ea', flexShrink: 0, marginLeft: '1rem' }} />}
              </div>
            </div>
          ))}
        </div>
      ) : activeSubTab === 'requests' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {demoRequests.filter(request => {
            // Apply search filter
            if (searchTerm) {
              const search = searchTerm.toLowerCase();
              const matchTitle = request.title?.toLowerCase().includes(search);
              const matchDesc = request.description?.toLowerCase().includes(search);
              const matchLabel = request.label_name?.toLowerCase().includes(search);
              if (!matchTitle && !matchDesc && !matchLabel) return false;
            }
            // Apply genre filter
            if (genreFilter !== 'all') {
              const hasGenre = request.genres?.some(g => g.toLowerCase().includes(genreFilter.toLowerCase()));
              if (!hasGenre) return false;
            }
            return true;
          }).length > 0 ? demoRequests.filter(request => {
            if (searchTerm) {
              const search = searchTerm.toLowerCase();
              const matchTitle = request.title?.toLowerCase().includes(search);
              const matchDesc = request.description?.toLowerCase().includes(search);
              const matchLabel = request.label_name?.toLowerCase().includes(search);
              if (!matchTitle && !matchDesc && !matchLabel) return false;
            }
            if (genreFilter !== 'all') {
              const hasGenre = request.genres?.some(g => g.toLowerCase().includes(genreFilter.toLowerCase()));
              if (!hasGenre) return false;
            }
            return true;
          }).map((request) => {
            const isRequestOwner = user && (
              user.id === request.user_id || 
              user.user_id === request.user_id ||
              user.id === request.created_by ||
              user.user_id === request.created_by
            );
            return (
            <div
              key={request.id}
              ref={highlightedRequestId === request.id ? (el) => el?.scrollIntoView({ behavior: 'smooth', block: 'center' }) : null}
              style={{
                background: highlightedRequestId === request.id ? 'rgba(251, 191, 36, 0.15)' : 'rgba(30, 20, 50, 0.5)',
                border: highlightedRequestId === request.id ? '2px solid #fbbf24' : '1px solid rgba(251, 191, 36, 0.2)',
                borderRadius: '12px',
                padding: '1.25rem',
                transition: 'all 0.3s ease',
                boxShadow: highlightedRequestId === request.id ? '0 0 20px rgba(251, 191, 36, 0.3)' : 'none',
                position: 'relative'
              }}
              onMouseEnter={(e) => { if (highlightedRequestId !== request.id) e.currentTarget.style.borderColor = 'rgba(251, 191, 36, 0.5)'; }}
              onMouseLeave={(e) => { if (highlightedRequestId !== request.id) e.currentTarget.style.borderColor = 'rgba(251, 191, 36, 0.2)'; }}
            >
              {/* Edit Button for Owner */}
              {isRequestOwner && (
                <button
                  onClick={() => setEditingRequest(request)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: 'rgba(251, 191, 36, 0.2)',
                    border: '1px solid rgba(251, 191, 36, 0.4)',
                    borderRadius: '6px',
                    padding: '0.3rem 0.6rem',
                    color: '#fbbf24',
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  ✏️ Edit
                </button>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <h4 style={{ color: '#fff', fontSize: '1rem', fontWeight: '600' }}>{request.title}</h4>
                    {request.verified && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(34, 197, 94, 0.2)', color: '#4ade80', padding: '2px 8px', borderRadius: '4px', fontSize: '0.65rem' }}>
                        <CheckCircle size={10} /> VERIFIED
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: '#fbbf24', fontSize: '0.85rem' }}>
                    <Building2 size={14} /> {request.label_name || request.user_name}
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem', marginBottom: '0.75rem', lineHeight: '1.5' }}>{request.description}</p>
                  {request.deadline && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginBottom: '0.75rem' }}>
                      <Calendar size={14} />
                      <span>Deadline: {request.deadline}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    {(request.genres || []).map((genre, index) => (
                      <span key={index} style={{ background: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', padding: '4px 10px', borderRadius: '6px', fontSize: '0.7rem' }}>{genre}</span>
                    ))}
                  </div>
                  {request.contact_email && (
                    <button
                      onClick={() => window.location.href = `mailto:${request.contact_email}?subject=Demo Submission - ${request.title}`}
                      style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '8px',
                        border: '1px solid #fbbf24',
                        background: 'transparent',
                        color: '#fbbf24',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      📧 Submit Demo
                    </button>
                  )}
                  
                  {/* Add to Calendar Link */}
                  {request.deadline && (
                    <button
                      onClick={() => handleAddToCalendar(request.id)}
                      disabled={addingToCalendar === request.id || addedToCalendar.includes(request.id)}
                      style={{
                        marginLeft: request.contact_email ? '0.75rem' : '0',
                        padding: '0.5rem 1rem',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'transparent',
                        color: addedToCalendar.includes(request.id) ? '#4ade80' : '#c4b5fd',
                        cursor: addedToCalendar.includes(request.id) ? 'default' : 'pointer',
                        fontSize: '0.8rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        textDecoration: addedToCalendar.includes(request.id) ? 'none' : 'underline',
                        opacity: addingToCalendar === request.id ? 0.6 : 1
                      }}
                    >
                      {addedToCalendar.includes(request.id) ? (
                        <>✓ Added to Gig & Event Hub</>
                      ) : addingToCalendar === request.id ? (
                        <>Adding...</>
                      ) : (
                        <>📅 Add to Event Calendar</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}) : (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <Building2 size={48} style={{ color: 'rgba(255,255,255,0.3)', marginBottom: '1rem' }} />
              <p style={{ color: 'rgba(255,255,255,0.5)' }}>No demo requests at the moment</p>
            </div>
          )}
        </div>
      ) : activeSubTab === 'contests' ? (
        <ContestsSection 
          remixContests={remixContests}
          searchTerm={searchTerm}
          genreFilter={genreFilter}
          contestAddingToCalendar={contestAddingToCalendar}
          contestAddedToCalendar={contestAddedToCalendar}
          handleAddContestToCalendar={handleAddContestToCalendar}
          currentUser={user}
          onEditContest={(contest) => setEditingContest(contest)}
          onDeleteContest={async (contestId) => {
            try {
              const token = localStorage.getItem('beatseek_token');
              const response = await fetch(`${BACKEND_URL}/api/remix-contests/${contestId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (response.ok) {
                loadContests();
              } else {
                const error = await response.json();
                alert(error.detail || 'Failed to delete contest');
              }
            } catch (error) {
              console.error('Error deleting contest:', error);
              alert('Failed to delete contest');
            }
          }}
          onRefreshContests={loadContests}
        />
      ) : null}

      {/* Demo Showcase Upload Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '1rem'
        }}>
          <div 
            className="demo-showcase-modal"
            style={{
              background: 'linear-gradient(135deg, rgba(20, 10, 35, 0.98), rgba(30, 15, 50, 0.98)) padding-box, linear-gradient(135deg, #9333ea, #fbbf24, #9333ea, #fbbf24) border-box',
              backgroundSize: '300% 300%',
              border: '3px solid transparent',
              borderRadius: '16px',
              padding: '2rem',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '85vh',
              overflowY: 'auto',
              animation: 'borderFlow 4s ease infinite'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: '#fbbf24', fontSize: '1.25rem', fontWeight: '600' }}>Share Your Demo</h3>
              <button onClick={resetShowcaseForm} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <input
                type="text"
                value={demoTitle}
                onChange={(e) => setDemoTitle(e.target.value)}
                placeholder="Track Title *"
                style={{
                  padding: '0.75rem 1rem',
                  background: 'rgba(30, 20, 50, 0.5)',
                  border: '1px solid rgba(251, 191, 36, 0.3)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.9rem'
                }}
              />
              <div style={{ position: 'relative' }}>
                <textarea
                  value={demoDescription}
                  onChange={(e) => setDemoDescription(e.target.value)}
                  placeholder="Description * (tell us about your track)"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    paddingRight: '3rem',
                    background: 'rgba(30, 20, 50, 0.5)',
                    border: '1px solid rgba(251, 191, 36, 0.3)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    resize: 'vertical'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowEmotePickerShowcase(!showEmotePickerShowcase)}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '0.75rem',
                    background: 'transparent',
                    border: 'none',
                    color: showEmotePickerShowcase ? '#fbbf24' : 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    padding: '0.25rem'
                  }}
                >
                  <Smile size={20} />
                </button>
                <EmotePicker 
                  isOpen={showEmotePickerShowcase} 
                  onEmoteSelect={handleEmoteSelectShowcase} 
                  onClose={() => setShowEmotePickerShowcase(false)} 
                />
              </div>
              <input
                type="text"
                value={demoGenre}
                onChange={(e) => setDemoGenre(e.target.value)}
                placeholder="Genre (e.g., Tech House)"
                style={{
                  padding: '0.75rem 1rem',
                  background: 'rgba(30, 20, 50, 0.5)',
                  border: '1px solid rgba(251, 191, 36, 0.3)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.9rem'
                }}
              />

              {/* Track Preview Section */}
              <div style={{
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                borderRadius: '12px',
                padding: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Music size={18} style={{ color: '#fbbf24' }} />
                    <span style={{ color: '#fff', fontWeight: '600' }}>Track Preview</span>
                    <span style={{ color: '#ef4444', fontSize: '0.75rem' }}>*Required</span>
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                    {userQuota.current}/{userQuota.max} active
                  </span>
                </div>

                {/* 7-Day Warning */}
                <div style={{
                  background: 'rgba(251, 191, 36, 0.1)',
                  border: '1px solid rgba(251, 191, 36, 0.3)',
                  borderRadius: '8px',
                  padding: '0.75rem',
                  marginBottom: '1rem'
                }}>
                  <p style={{ color: '#fbbf24', fontSize: '0.8rem', fontWeight: '600' }}>⚠️ 7-Day Preview Policy</p>
                  <p style={{ color: 'rgba(251, 191, 36, 0.8)', fontSize: '0.75rem' }}>
                    Share your 45-sec track preview for record labels and A&R reps to review. Max 2 active previews.
                  </p>
                </div>

                {audioUploadId ? (
                  <div style={{
                    background: 'rgba(251, 191, 36, 0.15)',
                    border: '1px solid rgba(251, 191, 36, 0.4)',
                    borderRadius: '8px',
                    padding: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}>
                    <div style={{ color: '#fbbf24' }}>✓</div>
                    <span style={{ color: '#fbbf24', fontSize: '0.875rem' }}>Track preview added</span>
                    <button
                      onClick={() => {
                        setAudioUploadId(null);
                        setAudioFile(null);
                        setAudioPreviewUrl(null);
                      }}
                      style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : !audioFile ? (
                  <div style={{
                    border: '2px dashed rgba(251, 191, 36, 0.4)',
                    borderRadius: '10px',
                    padding: '1.5rem',
                    textAlign: 'center'
                  }}>
                    <Music size={32} style={{ color: 'rgba(255,255,255,0.4)', marginBottom: '0.75rem' }} />
                    <p style={{ color: '#fff', fontWeight: '600', marginBottom: '0.25rem' }}>Drop your audio file here</p>
                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', marginBottom: '1rem' }}>
                      MP3, WAV, M4A, AAC • Max 10MB • Min 45 seconds
                    </p>
                    <input type="file" accept=".mp3,.wav,.m4a,.aac,audio/*" onChange={handleAudioFileSelect} id="demo-audio-upload" style={{ display: 'none' }} />
                    <label htmlFor="demo-audio-upload" style={{
                      display: 'inline-block',
                      padding: '0.5rem 1rem',
                      background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                      color: '#000',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      Choose File
                    </label>
                  </div>
                ) : (
                  <div style={{ background: 'rgba(30, 20, 50, 0.5)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '10px', padding: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <span style={{ color: '#fff', fontWeight: '600', fontSize: '0.85rem' }}>{audioFile.name}</span>
                      <button onClick={() => { setAudioFile(null); setAudioPreviewUrl(null); setAudioDuration(0); }} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}>
                        <X size={16} />
                      </button>
                    </div>

                    {audioPreviewUrl && (
                      <div>
                        <audio
                          ref={audioPlayerRef}
                          src={audioPreviewUrl}
                          onLoadedMetadata={(e) => {
                            setAudioDuration(e.target.duration);
                            setClipEndTime(Math.min(45, e.target.duration));
                          }}
                          controls
                          style={{ width: '100%', marginBottom: '0.75rem' }}
                        />

                        {audioDuration > 0 && (
                          <div style={{ background: 'rgba(251, 191, 36, 0.1)', borderRadius: '8px', padding: '0.75rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                              <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>Select 45-second clip:</span>
                              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>Total: {formatTime(audioDuration)}</span>
                            </div>
                            <div className="demo-slider-container" style={{ padding: '0 8px' }}>
                              <input
                                type="range"
                                min="0"
                                max={Math.max(0, Math.floor(audioDuration - 45))}
                                step="1"
                                value={Math.min(clipStartTime, Math.max(0, audioDuration - 45))}
                                onChange={(e) => {
                                  const maxStart = Math.max(0, audioDuration - 45);
                                  const start = Math.min(parseFloat(e.target.value), maxStart);
                                  setClipStartTime(start);
                                  setClipEndTime(Math.min(start + 45, audioDuration));
                                }}
                                style={{ 
                                  width: '100%', 
                                  marginBottom: '0.5rem',
                                  cursor: 'pointer'
                                }}
                              />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ color: '#fbbf24', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                🎵 {formatTime(clipStartTime)} → {formatTime(clipEndTime)} (45s)
                              </span>
                              <button onClick={playClipPreview} style={{
                                padding: '0.4rem 0.75rem',
                                background: '#fbbf24',
                                color: '#000',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                fontWeight: '600'
                              }}>
                                ▶ Preview
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', marginTop: '0.5rem' }}>
                      Size: {(audioFile.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                )}

                {/* Terms */}
                {!audioUploadId && audioFile && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginTop: '1rem' }}>
                    <input type="checkbox" id="demo-terms" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} style={{ marginTop: '3px' }} />
                    <label htmlFor="demo-terms" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>
                      I understand my track preview expires after 7 days. Max 2 active previews.
                    </label>
                  </div>
                )}

                {/* Upload Button */}
                {!audioUploadId && audioFile && (
                  <button
                    onClick={handleAudioUpload}
                    disabled={audioUploading || !termsAccepted}
                    style={{
                      width: '100%',
                      marginTop: '1rem',
                      padding: '0.75rem',
                      background: (audioUploading || !termsAccepted) ? 'rgba(251, 191, 36, 0.3)' : 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#000',
                      cursor: (audioUploading || !termsAccepted) ? 'not-allowed' : 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    {audioUploading ? 'Uploading...' : 'Upload Track Preview'}
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button onClick={resetShowcaseForm} style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'transparent',
                  color: 'rgba(255,255,255,0.7)',
                  cursor: 'pointer'
                }}>
                  Cancel
                </button>
                <button
                  onClick={submitDemoShowcase}
                  disabled={submitting || !demoTitle.trim() || !demoDescription.trim() || !audioUploadId}
                  style={{
                    padding: '0.75rem 1.5rem',
                    borderRadius: '10px',
                    border: 'none',
                    background: (submitting || !audioUploadId) ? 'rgba(251, 191, 36, 0.3)' : 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                    color: '#000',
                    cursor: (submitting || !audioUploadId) ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {submitting ? 'Posting...' : 'Post Demo'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Demo Request Modal (for Labels/A&R) */}
      {showRequestModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '1rem'
        }}>
          <div 
            className="demo-request-modal"
            style={{
              background: 'linear-gradient(135deg, rgba(20, 10, 35, 0.98), rgba(30, 15, 50, 0.98)) padding-box, linear-gradient(135deg, #9333ea, #fbbf24, #9333ea, #fbbf24) border-box',
              backgroundSize: '300% 300%',
              border: '3px solid transparent',
              borderRadius: '16px',
              padding: '2rem',
              width: '100%',
              maxWidth: '500px',
              animation: 'borderFlow 4s ease infinite'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: '#fbbf24', fontSize: '1.25rem', fontWeight: '600' }}>Post Demo Request</h3>
              <button onClick={resetRequestForm} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <input type="text" value={requestLabelName} onChange={(e) => setRequestLabelName(e.target.value)} placeholder="Label Name / A&R Name *"
                style={{ padding: '0.75rem 1rem', background: 'rgba(30, 20, 50, 0.5)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '10px', color: '#fff', fontSize: '0.9rem' }}
              />
              <input type="email" value={requestContactEmail} onChange={(e) => setRequestContactEmail(e.target.value)} placeholder="Contact Email * (for demo submissions)"
                style={{ padding: '0.75rem 1rem', background: 'rgba(30, 20, 50, 0.5)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '10px', color: '#fff', fontSize: '0.9rem' }}
              />
              <input type="text" value={requestTitle} onChange={(e) => setRequestTitle(e.target.value)} placeholder="Request Title *"
                style={{ padding: '0.75rem 1rem', background: 'rgba(30, 20, 50, 0.5)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '10px', color: '#fff', fontSize: '0.9rem' }}
              />
              <div style={{ position: 'relative' }}>
                <textarea value={requestDescription} onChange={(e) => setRequestDescription(e.target.value)} placeholder="Describe what kind of tracks you're looking for *" rows={4}
                  style={{ width: '100%', padding: '0.75rem 1rem', paddingRight: '3rem', background: 'rgba(30, 20, 50, 0.5)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '10px', color: '#fff', fontSize: '0.9rem', resize: 'vertical' }}
                />
                <button
                  type="button"
                  onClick={() => setShowEmotePickerRequest(!showEmotePickerRequest)}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '0.75rem',
                    background: 'transparent',
                    border: 'none',
                    color: showEmotePickerRequest ? '#fbbf24' : 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    padding: '0.25rem'
                  }}
                >
                  <Smile size={20} />
                </button>
                <EmotePicker 
                  isOpen={showEmotePickerRequest} 
                  onEmoteSelect={handleEmoteSelectRequest} 
                  onClose={() => setShowEmotePickerRequest(false)} 
                />
              </div>
              <input type="text" value={requestGenres} onChange={(e) => setRequestGenres(e.target.value)} placeholder="Genres (comma separated)"
                style={{ padding: '0.75rem 1rem', background: 'rgba(30, 20, 50, 0.5)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '10px', color: '#fff', fontSize: '0.9rem' }}
              />
              <input type="text" value={requestDeadline} onChange={(e) => setRequestDeadline(e.target.value)} placeholder="Deadline (e.g., December 31, 2025)"
                style={{ padding: '0.75rem 1rem', background: 'rgba(30, 20, 50, 0.5)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '10px', color: '#fff', fontSize: '0.9rem' }}
              />

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button onClick={resetRequestForm} style={{ padding: '0.75rem 1.5rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={submitDemoRequest} disabled={requestSubmitting} style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '10px',
                  border: 'none',
                  background: requestSubmitting ? 'rgba(251, 191, 36, 0.3)' : 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                  color: '#000',
                  cursor: requestSubmitting ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}>
                  {requestSubmitting ? 'Posting...' : 'Post Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remix Contest Modal */}
      {showContestModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '1rem'
        }}>
          <div 
            className="demo-showcase-modal"
            style={{
              background: 'linear-gradient(135deg, rgba(20, 10, 35, 0.98), rgba(30, 15, 50, 0.98)) padding-box, linear-gradient(135deg, #a78bfa, #fbbf24, #a78bfa, #fbbf24) border-box',
              backgroundSize: '300% 300%',
              border: '3px solid transparent',
              borderRadius: '16px',
              padding: '2rem',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '85vh',
              overflowY: 'auto',
              animation: 'borderFlow 4s ease infinite'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ color: '#a78bfa', fontSize: '1.25rem', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Trophy size={24} /> Post Contest
              </h3>
              <button
                onClick={() => setShowContestModal(false)}
                style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '0.5rem' }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', color: '#a78bfa', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Contest Title *</label>
                <input
                  type="text"
                  value={contestTitle}
                  onChange={(e) => setContestTitle(e.target.value)}
                  placeholder="e.g., Summer Production Challenge 2025"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(167, 139, 250, 0.3)',
                    background: 'rgba(30, 20, 50, 0.8)',
                    color: '#fff',
                    fontSize: '0.9rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#a78bfa', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Description</label>
                <textarea
                  value={contestDescription}
                  onChange={(e) => setContestDescription(e.target.value)}
                  placeholder="Describe your contest, what you're looking for, submission requirements..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(167, 139, 250, 0.3)',
                    background: 'rgba(30, 20, 50, 0.8)',
                    color: '#fff',
                    fontSize: '0.9rem',
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#a78bfa', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Genres (comma-separated)</label>
                <input
                  type="text"
                  value={contestGenres}
                  onChange={(e) => setContestGenres(e.target.value)}
                  placeholder="e.g., House, Techno, Tech House"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(167, 139, 250, 0.3)',
                    background: 'rgba(30, 20, 50, 0.8)',
                    color: '#fff',
                    fontSize: '0.9rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#a78bfa', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Submission Deadline *</label>
                <input
                  type="date"
                  value={contestDeadline}
                  onChange={(e) => setContestDeadline(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(167, 139, 250, 0.3)',
                    background: 'rgba(30, 20, 50, 0.8)',
                    color: '#fff',
                    fontSize: '0.9rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#a78bfa', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Prize</label>
                <input
                  type="text"
                  value={contestPrize}
                  onChange={(e) => setContestPrize(e.target.value)}
                  placeholder="e.g., Official release, $500, Free studio time"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(167, 139, 250, 0.3)',
                    background: 'rgba(30, 20, 50, 0.8)',
                    color: '#fff',
                    fontSize: '0.9rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#a78bfa', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Rules & Requirements</label>
                <textarea
                  value={contestRules}
                  onChange={(e) => setContestRules(e.target.value)}
                  placeholder="Submission format, length requirements, any restrictions..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(167, 139, 250, 0.3)',
                    background: 'rgba(30, 20, 50, 0.8)',
                    color: '#fff',
                    fontSize: '0.9rem',
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <button
                onClick={handleSubmitContest}
                disabled={contestSubmitting || !contestTitle.trim() || !contestDeadline}
                style={{
                  padding: '0.875rem',
                  borderRadius: '8px',
                  border: 'none',
                  background: contestSubmitting || !contestTitle.trim() || !contestDeadline 
                    ? 'rgba(167, 139, 250, 0.3)' 
                    : 'linear-gradient(135deg, #a78bfa, #9333ea)',
                  color: contestSubmitting || !contestTitle.trim() || !contestDeadline ? 'rgba(255,255,255,0.5)' : '#fff',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: contestSubmitting || !contestTitle.trim() || !contestDeadline ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {contestSubmitting ? <Loader2 size={18} className="animate-spin" /> : <Trophy size={18} />}
                {contestSubmitting ? 'Posting...' : 'Post Contest'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Contest Modal */}
      {editingContest && (
        <EditContestModal
          contest={editingContest}
          onClose={() => setEditingContest(null)}
          onSuccess={() => {
            setEditingContest(null);
            loadContests();
          }}
        />
      )}

      {/* Edit Demo Request Modal */}
      {editingRequest && (
        <EditDemoRequestModal
          request={editingRequest}
          onClose={() => setEditingRequest(null)}
          onSuccess={() => {
            setEditingRequest(null);
            loadRequests();
          }}
        />
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes borderFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .demo-showcase-modal::-webkit-scrollbar {
          width: 5px;
        }
        .demo-showcase-modal::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 10px;
          margin: 12px 0;
        }
        .demo-showcase-modal::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #9333ea, #fbbf24);
          border-radius: 10px;
        }
        .demo-slider-container input[type="range"] {
          -webkit-appearance: none;
          width: calc(100% - 24px);
          margin: 0 12px;
          height: 6px;
          border-radius: 3px;
          background: linear-gradient(90deg, #9333ea, #fbbf24);
          outline: none;
        }
        .demo-slider-container input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: linear-gradient(135deg, #fbbf24, #9333ea);
          cursor: pointer;
          border: 2px solid #fff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }
        .demo-slider-container input[type="range"]::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: linear-gradient(135deg, #fbbf24, #9333ea);
          cursor: pointer;
          border: 2px solid #fff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }
      `}</style>
    </div>
  );
};

export default DemoDropsTab;
